import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { Button, Dialog, Stack, useMediaQuery } from "@mui/material";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { memo, useEffect, useState } from "react";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import InfoIcon from "@mui/icons-material/Info";
import { ViewPrenotazione } from "./ViewPrenotazione";
import { Prenotazioni } from "../prismaTypes";
import { format } from "date-fns";
import { readDate } from "../utils/dateUtils";
import { useTheme } from "@mui/material/styles";
import { CoinManager } from "./CoinManager";
import { PrenotazioneData } from "./PrenotazioneData";

interface HeadCell {
  disablePadding: boolean;
  id: keyof PrenotazioneData;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
  sortable: boolean;
  small: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "",
    key: "ID",
    filter: false,
    sortable: false,
    small: true,
  },
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "Id",
    key: "ID2",
    filter: true,
    sortable: true,
    small: false,
  },
  {
    id: "dataInizioPrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Giorno",
    key: "Giorno",
    filter: true,
    sortable: true,
    small: false,
  },
  {
    id: "dataInizioPrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Inizio",
    key: "Inizio",
    filter: true,
    sortable: true,
    small: false,
  },
  {
    id: "dataFinePrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Fine",
    key: "Fine",
    filter: true,
    sortable: true,
    small: false,
  },
  {
    id: "denominazione",
    numeric: false,
    disablePadding: false,
    label: "Risorsa",
    key: "denominazioneris",
    filter: true,
    sortable: true,
    small: false,
  },
  {
    id: "luciBtn",
    numeric: false,
    disablePadding: false,
    label: "Luci",
    key: "luci",
    filter: false,
    sortable: false,
    small: false,
  },
  {
    id: "riscBtn",
    numeric: false,
    disablePadding: false,
    label: "Riscaldamento",
    key: "risc",
    filter: false,
    sortable: false,
    small: false,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof PrenotazioneData) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof PrenotazioneData, filter: string | number) => void;
  isSmall: boolean;
}

const EnhancedTableHead = memo(function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, isSmall } = props;
  const createSortHandler =
    (property: keyof PrenotazioneData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  // const createFilterHandler = (property: keyof Data) => (event: any) => {
  //   onFilter(event, property, event.target.value);
  // };

  return (
    <TableHead>
      <TableRow>
        {headCells
          .filter((h) => !isSmall || h.small)
          .map((headCell) => (
            <TableCell
              key={headCell.key}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.label !== "" && (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {headCell.sortable && (
                    <>
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </>
                  )}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
});

export const GestionePrenotazioni = memo(function GestionePrenotazioni(props: { altri: boolean }) {
  const { altri } = props;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const url = "prenotazioni/gestionePrenotazioni" + (altri ? "Altri/" : "/");

  const { isLoading, data } = useQuery<unknown, AxiosError, PrenotazioneData[]>(
    [url],
    () => axios.post(API_URL + url).then((res) => res.data),
    {
      refetchInterval: 5 * 60 * 1000,
    },
  );

  const rows = data ?? [];

  const [openView, setOpenView] = useState(false);
  const handleClickOpenView = () => setOpenView(true);
  const handleCloseView = () => setOpenView(false);
  const [viewId, setViewId] = useState<number>();

  const [openCoin, setOpenCoin] = useState(false);
  const handleClickOpenCoin = () => setOpenCoin(true);
  const handleCloseCoin = () => setOpenCoin(false);
  const [coinId, setCoinId] = useState<PrenotazioneData>();
  const [coinType, setCoinType] = useState("L");

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof PrenotazioneData>("id");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof PrenotazioneData,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof PrenotazioneData,
    filter: string | number,
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const viewPrenotazione = (id: Prenotazioni) => {
    setViewId(id.id);
    handleClickOpenView();
  };

  const openCoinManager = (data: PrenotazioneData, type: string) => {
    setCoinId(data);
    setCoinType(type);
    handleClickOpenCoin();
  };

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle(!altri ? "Le tue prenotazioni" : "Altre prenotazioni");
  }, [setTitle, altri]);

  if (isLoading) return <Loading />;

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <TableContainer>
          <Table size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              onFilter={handleFilterRequest}
              isSmall={isSmall}
            />
            <TableBody classes={{ root: "table-body" }}>
              {stableSort(rows, getComparator(order, orderBy), filters)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  return (
                    <>
                      {!isSmall && (
                        <>
                          <TableRow hover tabIndex={-1}>
                            <TableCell rowSpan={isSmall ? 3 : 1} sx={{ width: "20px" }}>
                              <IconButton
                                onClick={() => viewPrenotazione(row)}
                                sx={{ p: 0 }}
                                color="primary"
                              >
                                <InfoIcon />
                              </IconButton>
                              <br />
                              {isSmall && row.id}
                            </TableCell>
                            {!isSmall && <TableCell>{row.id}</TableCell>}
                            <TableCell>
                              {format(readDate(row.dataInizioPrenotazione), "dd/MM/yyyy")}
                            </TableCell>

                            <TableCell>
                              {format(readDate(row.dataInizioPrenotazione), "HH:mm")}
                            </TableCell>
                            <TableCell>
                              {format(readDate(row.dataFinePrenotazione), "HH:mm")}
                            </TableCell>
                            <TableCell>{row.denominazione}</TableCell>

                            <TableCell>
                              {row.luciBtn === 1 && (
                                <Button
                                  variant="contained"
                                  size="large"
                                  onClick={() => openCoinManager(row, "L")}
                                >
                                  Accensione Luci
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.riscBtn === 1 && (
                                <Button
                                  variant="contained"
                                  size="large"
                                  color="warning"
                                  onClick={() => openCoinManager(row, "R")}
                                >
                                  Accensione Riscaldamento
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      {isSmall && (
                        <TableRow hover tabIndex={-1} key={"H" + row.id}>
                          <TableCell colSpan={6}>
                            <Stack gap={1}>
                              <div onClick={() => viewPrenotazione(row)}>
                                {format(readDate(row.dataInizioPrenotazione), "dd/MM/yyyy")}
                              </div>
                              <div onClick={() => viewPrenotazione(row)}>
                                {format(readDate(row.dataInizioPrenotazione), "HH:mm")} {" - "}
                                {format(readDate(row.dataFinePrenotazione), "HH:mm")}
                              </div>
                              <div onClick={() => viewPrenotazione(row)}>{row.denominazione}</div>
                              {row.luciBtn === 1 && (
                                <Button
                                  variant="contained"
                                  size="large"
                                  fullWidth
                                  onClick={() => openCoinManager(row, "L")}
                                >
                                  Accensione Luci
                                </Button>
                              )}
                              {row.riscBtn === 1 && (
                                <Button
                                  variant="contained"
                                  size="large"
                                  color="warning"
                                  fullWidth
                                  onClick={() => openCoinManager(row, "R")}
                                >
                                  Accensione Riscaldamento
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      <Dialog open={openView} onClose={handleCloseView} fullScreen={isSmall}>
        {viewId && <ViewPrenotazione key={viewId} handleClose={handleCloseView} id={viewId} />}
      </Dialog>

      <Dialog open={openCoin} onClose={handleCloseCoin}>
        {coinId && <CoinManager handleClose={handleCloseCoin} data={coinId} type={coinType} />}
      </Dialog>
    </>
  );
});
